import type { ActivationResult, EndReason, RunnerDeps, StepView, Tour, TourAccessContext, TourActivation, TourStep } from '../types'
import { filterSteps, isStepEligible, resolveStepRoute } from './eligibility'
import { ENGINE_COPY } from './engine-copy'

export type { EndReason, RunnerDeps, StepView }

export const DEFAULT_WAIT_MS = 5000
/** After one activation, how long the next activator in the list may take to register (its view mounting). */
export const ACTIVATOR_WAIT_MS = 1500

/** A step's activations as a list (in order). */
export const activationsOf = (step: TourStep): TourActivation[] =>
  !step.activate ? [] : Array.isArray(step.activate) ? step.activate : [step.activate]
/**
 * While a step shows its "nothing to point at" note, keep watching (bounded) for
 * its target; if it appears, the step upgrades in place to the real highlight.
 */
export const UPGRADE_WAIT_MS = 20_000
/**
 * Page tours: before step 1, wait this long for the page's first anchor (the
 * page itself may still be loading a lazy chunk) — showing "One moment…". If
 * it never comes, end with a "still loading" message instead of a tour of notes.
 */
export const PAGE_READY_WAIT_MS = 15_000
/**
 * Once the page is up, a step whose target isn't there waits at most this long
 * before showing its note — the in-place upgrade catches anything later.
 */
export const SETTLED_WAIT_MS = 2500
/** How long to wait for the page state element (`Tour.state`) before starting. */
export const STATE_WAIT_MS = 15_000

/** Shown when the steps after the last one on screen turned out to be missing. */
const COMPLETE_STEP: TourStep = {
  id: '__complete',
  title: ENGINE_COPY.complete.title,
  body: ENGINE_COPY.complete.body,
}

/**
 * Framework-free tour state machine. Owns the step list, cross-route
 * navigation, bounded waits and access re-checks; rendering is delegated
 * (driver.js in the host, a spy in tests).
 *
 * Concurrency: every transition takes a fresh `token`; a transition whose
 * token is stale when its wait resolves does nothing. While a transition is
 * pending, Next/Back are ignored, so rapid clicks cannot double-advance, and a
 * re-filter keeps the in-flight destination instead of rewinding to the step
 * on screen.
 */
/**
 * A step a run drops before step 1: its view is registered but not allowed or
 * not available (E&M on an Operative chart). Not registered yet = the view that
 * owns it isn't mounted (a panel inside another tab): kept, it gets a note.
 */
export function droppedAtStart(s: TourStep, getActivator: RunnerDeps['getActivator']): boolean {
  if (s.route || !s.activate) return false
  return activationsOf(s).some((act) => {
    const a = getActivator?.(act.activator)
    return !!a && (!a.allowed.includes(act.value) || !a.isAvailable(act.value))
  })
}

/**
 * The steps a run of `tour` would start with right now — the same filters as
 * `applicable()` + `preResolve()` (access, `onlyOn`, page state, activator
 * availability). `state` is the page state read from `tour.state`; null while
 * the page hasn't published it yet → [] (not ready, the run would wait too).
 * The page card's "N steps" is this length, so it matches "1 of N".
 */
export function startSteps(
  tour: Tour,
  ctx: TourAccessContext,
  tours: readonly Tour[],
  path: string,
  state: string | null,
  getActivator: RunnerDeps['getActivator'],
): TourStep[] {
  if (tour.state && state === null) return []
  return filterSteps(tour, ctx, tours).filter(
    (s) =>
      (!s.onlyOn || s.onlyOn.test(path)) &&
      (!s.states || !tour.state || (state !== null && s.states.includes(state))) &&
      !droppedAtStart(s, getActivator),
  )
}

export class TourRunner {
  private tour: Tour | null = null
  private steps: TourStep[] = []
  private index = -1
  private token = 0
  private abort: AbortController | null = null
  private pending = false
  /** Step the in-flight transition is heading for (its id), while pending. */
  private pendingStepId: string | null = null
  /** Path the in-flight transition asked the router for (null = staying put). */
  private expectPath: string | null = null
  /** Path when the in-flight transition began. */
  private transitionFromPath = ''
  private shownAny = false
  private shownAtPath = ''
  /** Step ids skipped because their target never appeared (excluded from the count). */
  private missing = new Set<string>()
  /** The closing "Tour complete" card is on screen. */
  private completing = false
  /** Page state (`Tour.state`), read when the run starts. */
  private state: string | null = null
  /** Views the run switched, with the user's original value and where. */
  private originals = new Map<string, { value: string; path: string }>()
  /** The view (activator → value) each shown step was seen in, for Back. */
  private viewAt = new Map<string, Map<string, string>>()
  /** Steps found unavailable when the run started — out of the count for good. */
  private dropped = new Set<string>()
  /** Background watch for a "missing" step's target (see UPGRADE_WAIT_MS). */
  private watch: AbortController | null = null
  /** A `restoreOnLeave` activation of the step on screen: the view to put back when leaving it. */
  private leaveRestore: { name: string; value: string; path: string }[] = []
  private readonly deps: RunnerDeps

  constructor(deps: RunnerDeps) {
    this.deps = deps
  }

  get activeTourId(): string | null {
    return this.tour?.id ?? null
  }

  get currentStepId(): string | null {
    return this.steps[this.index]?.id ?? null
  }

  get isPending(): boolean {
    return this.pending
  }

  /** Returns false (and reports `unavailable`) when the user has no eligible step. */
  start(tour: Tour, fromStepId?: string): boolean {
    if (this.tour) this.end('aborted')
    const steps = this.applicable(tour, true)
    if (steps.length === 0) {
      this.deps.onEnd(tour, 'unavailable', false)
      return false
    }
    this.tour = tour
    this.steps = steps
    this.index = -1
    this.shownAny = false
    this.completing = false
    this.missing.clear()
    this.originals.clear()
    this.viewAt.clear()
    this.dropped.clear()
    this.state = null
    this.expectPaths()
    void this.begin(tour, fromStepId)
    return true
  }

  /**
   * The ONE place that records which locations belong to the run right now:
   * the path it is on, plus a route it is navigating to. Called by every
   * transition into a pending/active state (start / resume, the start-up waits,
   * each step transition, step navigation) so `onLocationChange` never mistakes
   * the run's own page for the user navigating away.
   */
  private expectPaths(navigatingTo: string | null = null): void {
    this.transitionFromPath = this.deps.currentPath()
    this.expectPath = navigatingTo
  }

  /** The page is showing a load error / "not available" / 403 instead of itself. */
  private pageUnavailable(tour: Tour): boolean {
    const sel = this.deps.unavailableSelector
    return tour.kind === 'page' && !!sel && !!this.deps.isPresent?.(sel)
  }

  /** Wait (bounded) for `selector` OR an unavailable-page marker, whichever comes first. */
  private async waitReady(tour: Tour, selector: string, ms: number): Promise<boolean> {
    const token = ++this.token
    const ac = new AbortController()
    this.abort = ac
    this.expectPaths()
    this.setPending(true)
    const marker = tour.kind === 'page' ? this.deps.unavailableSelector : undefined
    await this.deps.waitFor(marker ? `${selector}, ${marker}` : selector, ms, ac.signal)
    if (token !== this.token || this.tour !== tour) return false
    this.setPending(false)
    return true
  }

  /**
   * Fix the step list before step 1 shows, so "n of m" never shrinks. Only
   * DEFINITIVE reasons drop a step here — the page says the view is unavailable
   * (E&M on an Operative chart) or not allow-listed. A target that is merely not
   * mounted yet (data still loading, a lazy header button, a resume right after
   * refresh) is kept: it gets its bounded wait and, if it never comes, a short
   * note in place. Access, page state and `onlyOn` are already applied by
   * `applicable()`.
   */
  private preResolve(): void {
    for (const s of this.steps) if (droppedAtStart(s, this.deps.getActivator)) this.dropped.add(s.id)
    this.steps = this.steps.filter((s) => !this.dropped.has(s.id))
  }

  private async begin(tour: Tour, fromStepId?: string): Promise<void> {
    if (this.pageUnavailable(tour)) return this.end('page_unavailable')
    if (tour.state && this.deps.readState) {
      this.state = this.deps.readState(tour.state)
      if (this.state === null) {
        if (!(await this.waitReady(tour, tour.state.selector, STATE_WAIT_MS))) return
        this.state = this.deps.readState(tour.state)
        // No page state after the wait (failed load, error state): there is
        // nothing to guide — end now rather than wait again on each step.
        if (this.pageUnavailable(tour)) return this.end('page_unavailable')
        // Element there but no state → nothing to tour; element never came → still loading.
        if (this.state === null) return this.end(this.deps.isPresent?.(tour.state.selector) ? 'unavailable' : 'page_loading')
      }
      this.steps = this.applicable(tour)
    } else {
      // Let the page show its first anchor (bounded) before judging what's there.
      // "Is the page up?" is judged on the tour's FIRST anchor (e.g. the page
      // header) — not on the step being resumed, whose target may legitimately
      // never render (that one gets its own wait + note + upgrade).
      const first = this.steps.find((s) => s.target && !s.route && !s.activate)
      if (tour.kind === 'page' && first?.target && !first.route && !first.activate && !this.deps.isPresent?.(first.target)) {
        if (!(await this.waitReady(tour, first.target, Math.max(PAGE_READY_WAIT_MS, first.waitMs ?? DEFAULT_WAIT_MS)))) return
        if (this.pageUnavailable(tour)) return this.end('page_unavailable')
        // Still no page after the bounded wait: don't run a tour of notes over a spinner.
        if (!this.deps.isPresent?.(first.target)) return this.end('page_loading')
      }
    }
    this.preResolve()
    if (this.steps.length === 0) return this.end('unavailable')
    const from = fromStepId ? this.steps.findIndex((s) => s.id === fromStepId) : 0
    void this.goTo(Math.max(0, from), 1)
  }

  next(): void {
    if (!this.tour || this.pending) return
    if (this.completing) return this.end('completed')
    // Finish on the last step ends here; only unseen later steps are attempted.
    if (this.index >= this.steps.length - 1) return this.end('completed')
    void this.goTo(this.index + 1, 1)
  }

  back(): void {
    if (!this.tour || this.pending) return
    if (this.completing) {
      this.completing = false
      return void this.goTo(this.index, 1)
    }
    if (this.index <= 0) return
    void this.goTo(this.index - 1, -1)
  }

  /** User closed the tour (Esc / ×). Closing the "Tour complete" card is finishing it. */
  close(): void {
    if (this.tour) this.end(this.completing ? 'completed' : 'skipped')
  }

  /** External teardown (logout, user switch, unmount). */
  stop(reason: EndReason = 'aborted'): void {
    if (this.tour) this.end(reason)
  }

  /**
   * Access / client context changed: recompute the eligible list. The step that
   * matters is the in-flight destination if a transition is pending, else the
   * step on screen. If it survived, a pending transition simply continues and a
   * shown step is re-run (re-waiting — its data may be refetching); if it is
   * gone, continue at the next surviving step; if none, end.
   */
  refilter(): void {
    const tour = this.tour
    if (!tour) return
    const fresh = this.applicable(tour)
    if (fresh.length === 0) return this.end('access_lost')
    const anchorId = this.pending ? this.pendingStepId : (this.steps[this.index]?.id ?? null)
    const anchorOrder = anchorId ? tour.steps.findIndex((s) => s.id === anchorId) : -1
    const shownId = this.steps[this.index]?.id
    this.steps = fresh
    this.index = shownId ? fresh.findIndex((s) => s.id === shownId) : -1

    if (this.pending && anchorId && fresh.some((s) => s.id === anchorId)) return
    let target = fresh.findIndex((s) => s.id === anchorId)
    if (target < 0) target = fresh.findIndex((s) => tour.steps.indexOf(s) > anchorOrder)
    if (target < 0) return this.end(this.shownAny ? 'access_lost' : 'unavailable')
    void this.goTo(target, 1)
  }

  /**
   * The router committed `path`. Commits the engine asked for are expected; any
   * other change (browser Back, a link, a redirect) ends the run rather than
   * showing guidance on a page it wasn't written for — also mid-transition.
   */
  onLocationChange(path: string): void {
    if (!this.tour) return
    if (this.pending) {
      // The run's own page (or the route it's navigating to) is never "away".
      if (path !== this.expectPath && path !== this.transitionFromPath) this.end('aborted')
      return
    }
    if (this.shownAny && path !== this.shownAtPath) this.end('aborted')
  }

  private async goTo(start: number, dir: 1 | -1): Promise<void> {
    const tour = this.tour
    if (!tour) return
    const token = ++this.token
    this.abort?.abort()
    this.stopWatch()
    this.restoreLeftView()
    const ac = new AbortController()
    this.abort = ac
    const steps = this.steps
    this.completing = false
    this.expectPaths()
    this.setPending(true)

    try {
      for (let i = start; i >= 0 && i < steps.length; i += dir) {
        const step = steps[i]
        // Going back never re-waits on a target that already failed to appear.
        if (dir === -1 && this.missing.has(step.id)) continue
        this.pendingStepId = step.id
        const ctx = this.deps.getContext()
        // Re-check right before showing: access may have changed since start.
        if (!isStepEligible(tour, step, ctx, this.deps.tours)) continue

        const route = resolveStepRoute(step, ctx.access)
        const here = this.deps.currentPath()
        if (route && route !== here) {
          if (!this.deps.canLeave(here)) {
            // Never navigate out of a create/edit flow: explain instead.
            this.show(tour, step, null, i, true)
            return
          }
          this.expectPaths(route)
          const committed = await this.deps.navigate(route, ac.signal)
          if (token !== this.token) return
          if (!committed) return this.end('unavailable')
        }
        // A page tour's steps only make sense on its own page.
        if (tour.match && !tour.match.test(this.deps.currentPath())) return this.end('aborted')

        if (step.onlyOn && !step.onlyOn.test(this.deps.currentPath())) continue
        if (step.states && !this.stateMatches(tour, step)) continue

        let blocked: 'missing' | 'blocked' | null = null
        const acts = activationsOf(step)
        for (let k = 0; k < acts.length && !blocked; k++) {
          const act = acts[k]
          // A later activator may belong to a view the earlier one mounts: wait (bounded).
          if (k > 0 && !(await this.waitActivator(act.activator, ac.signal))) {
            if (token !== this.token) return
            blocked = 'missing'
            break
          }
          if (token !== this.token) return
          const before = this.deps.getActivator?.(act.activator)?.current()
          const result = this.activate(act.activator, act.value)
          if (result === 'ok' && act.restoreOnLeave && before !== undefined) {
            this.leaveRestore.push({ name: act.activator, value: before, path: this.deps.currentPath() })
          }
          if (result === 'unavailable') blocked = 'missing'
          if (result === 'blocked') blocked = 'blocked'
        }
        if (blocked) {
          this.show(tour, step, null, i, false, blocked)
          return
        }
        if (dir === -1) {
          // Back: put the page in the view this step was seen in going forward
          // (its own activator excepted — that was just applied above).
          this.reapplyView(step.id, new Set(activationsOf(step).map((x) => x.activator)))
        }

        // Present but not something we can point at (CSS-hidden / off-canvas),
        // decided per step: show it centred with its fallback copy. Not after an
        // activation — the switched view may still be rendering.
        if (!route && !acts.length && step.target && this.deps.isHiddenNow?.(step.target)) {
          this.show(tour, step, null, i, false, 'hidden')
          return
        }

        let element: Element | null = null
        if (step.target) {
          // One bounded wait per transition: a missing target is shown as a
          // note, never skipped, so no chain of waits can build up.
          // The page may declare "this isn't here" early (an empty / error state
          // marked `data-tour-missing="<id>"`): resolve on whichever comes first.
          const id = /data-tour="([^"]+)"/.exec(step.target)?.[1]
          const either = id ? `${step.target}, [data-tour-missing="${id}"]` : step.target
          // Once the page is up (a step has been shown and we stay on this page),
          // waits are short: the in-place upgrade catches a target that's late.
          const want = step.waitMs ?? DEFAULT_WAIT_MS
          const settled = this.shownAny && !this.expectPath
          element = await this.deps.waitFor(either, settled ? Math.min(want, SETTLED_WAIT_MS) : want, ac.signal)
          if (token !== this.token) return
          if (element?.hasAttribute('data-tour-missing')) element = null
          if (!element) {
            // Never skip mid-run (the count would shrink): say it isn't there,
            // and keep watching so a late target upgrades the step in place.
            this.show(tour, step, null, i, false, 'missing')
            this.watchForLate(tour, step, token)
            return
          }
        }
        this.show(tour, step, element, i, false)
        return
      }

      if (token !== this.token) return
      if (dir === 1) {
        if (!this.shownAny) return this.end('unavailable')
        // The remaining steps were missing: close with an explicit end card
        // instead of vanishing mid-count.
        this.completing = true
        this.setPending(false)
        const total = this.steps.filter((s) => !this.missing.has(s.id)).length
        this.deps.render({ tour, step: COMPLETE_STEP, element: null, position: total, total, isFirst: false, isLast: true })
      } else if (this.index >= 0) {
        // Nothing earlier is available: restore the step we came from.
        void this.goTo(this.index, 1)
      }
    } finally {
      if (token === this.token && this.tour === tour && this.pending) this.setPending(false)
    }
  }

  private stateMatches(tour: Tour, step: TourStep): boolean {
    if (!step.states) return true
    // Re-read: the page state can change during a run (e.g. data arriving).
    const now = tour.state && this.deps.readState ? this.deps.readState(tour.state) : null
    if (now !== null) this.state = now
    return this.state !== null && step.states.includes(this.state)
  }

  /**
   * Switch a page view through its registered activator. Remembers the user's
   * original value the first time so it can be put back. Never a DOM click.
   */
  private activate(name: string, value: string): ActivationResult {
    const a = this.deps.getActivator?.(name)
    if (!a || !a.allowed.includes(value) || !a.isAvailable(value)) return 'unavailable'
    const current = a.current()
    if (current === value) return 'ok'
    // The page's "would this discard input?" check guards the USER's view. A view
    // the tour itself switched to holds nothing typed (the overlay blocks input).
    if (!this.originals.has(name) && !a.canSwitch()) return 'blocked'
    if (!this.originals.has(name)) this.originals.set(name, { value: current, path: this.deps.currentPath() })
    a.set(value)
    return 'ok'
  }

  /**
   * The step is on screen as a "missing" note. If its target turns up within
   * UPGRADE_WAIT_MS, re-render the SAME step (same position / total) anchored on
   * it with its real copy. Any transition, close or end bumps the token and
   * aborts the watch, so a late target can never upgrade the wrong step.
   */
  private watchForLate(tour: Tour, step: TourStep, token: number): void {
    if (!step.target) return
    const ac = new AbortController()
    this.watch = ac
    // Keep watching a present-but-hidden target too (it can become visible once a
    // resize / relayout settles), not just an absent one.
    void this.deps.waitFor(step.target, UPGRADE_WAIT_MS, ac.signal, { hiddenGraceMs: UPGRADE_WAIT_MS }).then((el) => {
      if (this.watch === ac) this.watch = null
      if (!el || ac.signal.aborted || token !== this.token || this.tour !== tour || this.pending) return
      if (this.steps[this.index] !== step) return
      this.deps.render({ ...this.view(tour, step, el), state: this.state, upgraded: true })
    })
  }

  /** Leaving a step whose activation was `restoreOnLeave`: put that view back now. */
  private restoreLeftView(): void {
    // Innermost first (e.g. the code selection, then the stage it sits in).
    const list = this.leaveRestore.reverse()
    this.leaveRestore = []
    for (const r of list) {
      if (r.path !== this.deps.currentPath()) continue // another chart: it has its own view
      const a = this.deps.getActivator?.(r.name)
      if (a && a.current() !== r.value && a.isAvailable(r.value)) a.set(r.value)
    }
  }

  /** Poll (bounded) until `name` is registered — its view mounts after an earlier switch. */
  private async waitActivator(name: string, signal: AbortSignal): Promise<boolean> {
    const until = Date.now() + ACTIVATOR_WAIT_MS
    while (!this.deps.getActivator?.(name)) {
      if (signal.aborted || Date.now() >= until) return false
      await new Promise((r) => setTimeout(r, 50))
    }
    return !signal.aborted
  }

  private stopWatch(): void {
    this.watch?.abort()
    this.watch = null
  }

  /** Back to a step without its own `activate`: restore the view it was shown in. */
  private reapplyView(stepId: string, except?: Set<string>): void {
    const seen = this.viewAt.get(stepId)
    if (!seen) return
    for (const [name, value] of seen) {
      if (except?.has(name)) continue
      const a = this.deps.getActivator?.(name)
      if (!a || a.current() === value) continue
      // The user's own original view may not be on the allow-list (e.g. Activity Log).
      if (this.originals.get(name)?.value === value) a.set(value)
      else this.activate(name, value)
    }
  }

  /** Put every switched view back — only on the same page (another chart resets itself). */
  private restoreViews(): void {
    for (const [name, { value, path }] of this.originals) {
      const a = this.deps.getActivator?.(name)
      if (a && path === this.deps.currentPath() && a.current() !== value && a.isAvailable(value)) a.set(value)
    }
    this.originals.clear()
  }

  private show(
    tour: Tour,
    step: TourStep,
    element: Element | null,
    i: number,
    blocked: boolean,
    centred?: 'hidden' | 'blocked' | 'missing',
  ): void {
    this.missing.delete(step.id)
    // `this.steps` may have been re-filtered while we waited; index into it.
    const at = this.steps.indexOf(step)
    this.index = at >= 0 ? at : i
    this.shownAny = true
    this.shownAtPath = this.deps.currentPath()
    this.recordView(step.id)
    this.setPending(false)
    this.deps.render({
      ...this.view(tour, step, element),
      blockedNavigation: blocked || undefined,
      centred,
      state: this.state,
    })
    this.deps.onStepShown?.(tour, step, this.shownAtPath)
  }

  /** Access-eligible steps that apply to the page the user is on. */
  private applicable(tour: Tour, stateUnknownOk = false): TourStep[] {
    const path = this.deps.currentPath()
    const knowsState = !tour.state || !this.deps.readState
    return filterSteps(tour, this.deps.getContext(), this.deps.tours).filter(
      (s) =>
        !this.dropped.has(s.id) &&
        (!s.onlyOn || s.onlyOn.test(path)) &&
        // Until the page state is known, state-specific steps wait (begin() re-filters).
        (!s.states || knowsState || stateUnknownOk || (this.state !== null && s.states.includes(this.state))),
    )
  }

  /** Remember the views (every activator the tour uses) this step is shown in. */
  private recordView(stepId: string): void {
    const names = new Set(this.steps.flatMap((s) => activationsOf(s).map((x) => x.activator)))
    const seen = new Map<string, string>()
    for (const name of names) {
      const a = this.deps.getActivator?.(name)
      if (a) seen.set(name, a.current())
    }
    this.viewAt.set(stepId, seen)
  }

  private view(tour: Tour, step: TourStep, element: Element | null): StepView {
    const visible = this.steps.filter((s) => !this.missing.has(s.id))
    const position = visible.indexOf(step) + 1
    return {
      tour,
      step,
      element,
      position,
      total: visible.length,
      isFirst: position === 1,
      isLast: position === visible.length,
    }
  }

  private setPending(pending: boolean): void {
    this.pending = pending
    if (!pending) {
      this.pendingStepId = null
      this.expectPath = null
    }
    this.deps.setPending(pending)
  }

  private end(reason: EndReason): void {
    const tour = this.tour
    if (!tour) return
    this.token++
    this.abort?.abort()
    this.abort = null
    this.stopWatch()
    this.restoreLeftView()
    const shownAny = this.shownAny
    this.tour = null
    this.steps = []
    this.index = -1
    this.shownAny = false
    this.completing = false
    this.missing.clear()
    this.setPending(false)
    // Every exit path (finish, Esc, access loss, navigation, unmount) restores
    // the views the tour switched.
    this.restoreViews()
    this.viewAt.clear()
    this.dropped.clear()
    this.state = null
    this.deps.onEnd(tour, reason, shownAny)
  }
}
